import { useEffect, useRef, useState } from "react";
import { QRCodeCanvas } from "qrcode.react";

/**
 * A component that generates a QR code for a restaurant's URL with customized branding.
 * It renders the QR code and additional branding on an HTML canvas, allowing users to download the
 * generated QR code image.
 *
 * @component
 * @param {Object} props - Component properties.
 * @param {string} props.restaurantName - The name of the restaurant to be displayed.
 * @param {string} props.restaurantURL - The URL of the restaurant to be encoded in the QR code.
 * @param {number} [props.canvasWidth] - The width of the canvas in pixels. Default is 400.
 * @param {number} [props.canvasHeight] - The height of the canvas in pixels. Default is 520.
 * @param {number} [props.qrCodeSize] - The size of the QR code in pixels. Default is 300.
 *
 * @example
 * <QRCode restaurantName={"watami"} restaurantURL={"https://tastelens.com/watami"} />
 *
 * @returns {JSX.Element} The rendered QRCode component.
 */
const QRCode = ({ restaurantName, restaurantURL, canvasWidth=400, canvasHeight=520, qrCodeSize=300}) => {
  const [imageUrl, setImageUrl] = useState(null);
  const qrCanvasRef = useRef(null);
  const previewCanvasRef = useRef(null);

  useEffect(() => {
    const generateImage = () => {
      const qrCanvas = qrCanvasRef.current;
      const previewCanvas = previewCanvasRef.current;
      const ctx = previewCanvas.getContext("2d");

      previewCanvas.width = canvasWidth;
      previewCanvas.height = canvasHeight;

      ctx.clearRect(0, 0, previewCanvas.width, previewCanvas.height);
      ctx.textAlign = "center";

      // draw a white background
      ctx.fillStyle = "white";
      ctx.fillRect(0, 0, previewCanvas.width, previewCanvas.height);

      // draw the restaurant name at the top
      ctx.fillStyle = "black";
      ctx.font = "20px Hiragino Kaku Gothic StdN";
      const restaurantText = restaurantName || "no restaurant name provided";
      ctx.fillText(restaurantText, previewCanvas.width / 2, 60);

      // draw the QR code in the middle
      const qrX = (previewCanvas.width - qrCodeSize) / 2;
      ctx.drawImage(qrCanvas, qrX, 100, qrCodeSize, qrCodeSize);

      // draw "powered by tastelens" at the bottom
      const poweredByX = 125;
      ctx.font = "16px Hiragino Maru Gothic Pro";
      ctx.fillText("powered by", poweredByX, 468);
      ctx.font = "22px SuperDream";
      ctx.fillStyle = "#6DC9EB";
      ctx.fillText("tastelens", poweredByX + 106, 470);

      const tastelensLogo = new Image();
      tastelensLogo.src = "/logo.png";
      tastelensLogo.onload = () => {
        ctx.drawImage(tastelensLogo, poweredByX + 167, 448, 30, 30);
        const dataUrl = previewCanvas.toDataURL("image/png");
        setImageUrl(dataUrl);
      };
    };

    if (qrCanvasRef.current) {
      generateImage();
    }
  }, [restaurantURL, restaurantName]);

  const handleDownload = () => {
    const link = document.createElement("a");
    link.href = imageUrl;
    link.download = "restaurant_qr_code.png";
    link.click();
  };

  return (
    <div className="QRCode">
      <QRCodeCanvas
        ref={qrCanvasRef}
        value={restaurantURL || "https://google.com"}
        size={256}
        style={{ display: "none" }} // hide the QR code canvas
      />

      <div className="QRCode-container">
        <canvas className="QRCode-img" ref={previewCanvasRef} />
      </div>

      <button className="QRCode-download-btn" onClick={handleDownload}>
        download qr code
      </button>
    </div>
  );
};

export default QRCode;
